import React, { useEffect, useState } from "react";
import CustomInput from "../../../../../../GlobalComponents/CustomInput/CustomInput";
import Dropdown from "../../../../../../GlobalComponents/Dropdown/Dropdown";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import IconButton from "../../../../../../GlobalComponents/IconButton/IconButton";
import { Grid2 } from "@mui/material";
import styles from "./InviteMemberStyles.module.scss";

function InviteMember({
  emailVals,
  role,
  roleOptions,
  setEmailValues,
  setRole,
  members,
}) {
  const maxInvite = process.env.REACT_APP_ORG_USER_INVITE_LIMIT;
  const [emailErrors, setEmailErrors] = useState(
    Array(emailVals.length).fill(false)
  );
  const [roleErrors, setRoleErrors] = useState(Array(role.length).fill(false));
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [roleErrorMessage, setRoleErrorMessage] = useState("");
  const [maxEmailsMessage, setMaxEmailsMessage] = useState("");
  const roleOption = roleOptions.map((role) => ({
    value: role.roleId,
    label: role.label,
  }));

  useEffect(() => {
    setEmailErrors(Array(emailVals.length).fill(false));
  }, [emailVals.length]);

  // add a new email input
  const handleAddEmail = () => {
    const lastEmail = emailVals[emailVals.length - 1];
    const lastRole = role[emailVals.length - 1];

    if (!lastEmail) {
      setEmailErrorMessage("Please Enter Email Id");
      const newEmailErrors = Array.isArray(emailErrors)
        ? [...emailErrors]
        : Array(emailVals.length).fill(false);
      newEmailErrors[emailVals.length - 1] = true;
      setEmailErrors(newEmailErrors);
      return;
    }

    if (!lastRole) {
      setRoleErrorMessage("Please select a role");
      const newRoleErrors = [...roleErrors];
      newRoleErrors[emailVals.length - 1] = true;
      setRoleErrors(newRoleErrors);
      return;
    }

    const isValid = validateEmail(lastEmail);

    // Check for duplicates in emailVals and members array
    const isDuplicate =
      emailVals.filter((email) => email === lastEmail).length > 1 ||
      members.some((member) => member.emailId === lastEmail);

    if (isValid && !isDuplicate) {
      if (emailVals.length < maxInvite) {
        setEmailValues([...emailVals, ""]);
        setEmailErrors([...emailErrors, false]);
        setRole([...role, ""]);
        setRoleErrors([...roleErrors, false]);
        setEmailErrorMessage("");
        setRoleErrorMessage("");
        setMaxEmailsMessage("");
      } else if (emailVals.length === maxInvite) {
        setMaxEmailsMessage("Maximum of 5 invitations can be sent at once.");
      }
    } else {
      const newEmailErrors = Array.isArray(emailErrors)
        ? [...emailErrors]
        : Array(emailVals.length).fill(false);
      newEmailErrors[emailVals.length - 1] = true;
      setEmailErrors(newEmailErrors);

      // Set error message based on duplicate check
      const errorMessage = members.some(
        (member) => member.emailId === lastEmail
      )
        ? "Can't invite already existing members"
        : "Invalid email address"; // Add other conditions as needed

      setEmailErrorMessage(errorMessage); // Assuming you have this state to store the error message
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // remove an email input
  const handleRemoveEmail = (index) => {
    const newEmailVals = [...emailVals];
    newEmailVals.splice(index, 1);
    setEmailValues(newEmailVals);
    const newEmailErrors = Array.isArray(emailErrors)
      ? [...emailErrors]
      : Array(emailVals.length).fill(false);
    newEmailErrors.splice(index, 1);
    setEmailErrors(newEmailErrors);

    const newRoles = [...role];
    newRoles.splice(index, 1);
    setRole(newRoles);

    const newRoleErrors = [...roleErrors];
    newRoleErrors.splice(index, 1);
    setRoleErrors(newRoleErrors);

    if (
      newEmailErrors.every((error) => !error) &&
      newRoleErrors.every((error) => !error)
    ) {
      setEmailErrorMessage("");
      setRoleErrorMessage("");
      setMaxEmailsMessage("");
    }
  };

  // update email input
  const handleEmailChange = (index, value) => {
    const trimmedValue = value.replace(/\s+/g, "");

    const newEmailVals = [...emailVals];
    newEmailVals[index] = trimmedValue;
    setEmailValues(newEmailVals);

    // Validate the email format
    const isValid = validateEmail(value);

    // Check for duplicates (ignore the current field during typing)
    const isDuplicate =
      (value !== "" &&
        newEmailVals.filter((email, idx) => email === value && idx !== index)
          .length > 0) ||
      members.some((member) => member.emailId === value);

    const newEmailErrors = Array.isArray(emailErrors)
      ? [...emailErrors]
      : Array(emailVals.length).fill(false);
    newEmailErrors[index] = !isValid || isDuplicate; // Mark as error if invalid or duplicate
    setEmailErrors(newEmailErrors);

    if (isDuplicate) {
      if (members.some((member) => member.emailId === value)) {
        setEmailErrorMessage("Email already exists!");
      } else {
        setEmailErrorMessage("Duplicate email not allowed");
      }
    } else if (!isValid && value !== "") {
      setEmailErrorMessage("Invalid email address");
    } else {
      setEmailErrorMessage("");
    }
  };

  const roleArray = Array.isArray(role) ? role : [];
  const handleRoleChange = (index, value) => {
    const newRoles = [...roleArray];
    newRoles[index] = value;
    setRole(newRoles);
    if (value) {
      const newRoleErrors = [...roleErrors];
      newRoleErrors[index] = false;
      setRoleErrors(newRoleErrors);
      setRoleErrorMessage(""); // Clear the role error message
    }
  };

  return (
    <>
      <Grid2 className={styles.container}>
        {emailVals.map((email, index) => (
          <>
            <Grid2 key={index}>
              <Grid2 className={styles.emailContainer}>
                <CustomInput
                  placeholder="Enter Email Id"
                  value={email.toLowerCase()}
                  setValue={(value) => handleEmailChange(index, value)}
                  error={email !== "" && emailErrors[index]}
                  className={styles.emailInput}
                  inputStyle={styles.emailInputheight}
                />
                <Dropdown
                  placeholder="Select a role"
                  options={roleOption}
                  value={role[index]}
                  onSelect={(value) => handleRoleChange(index, value)}
                  className={styles.dropdownBox}
                  error={roleErrors[index]}
                  dropdownHeader={styles.dropdown}
                  multiple={false}
                />
                {/* Show RemoveIcon only if there are more than one email */}
                {emailVals.length > 1 && (
                  <IconButton
                    className={styles.icon}
                    icon={RemoveIcon}
                    onClick={() => handleRemoveEmail(index)}
                  />
                )}
                {emailVals.length === 1 && (
                  <IconButton
                    className={styles.icon}
                    icon={AddIcon}
                    onClick={handleAddEmail}
                  />
                )}
              </Grid2>
            </Grid2>
            <Grid2 className={styles.errorText}>
              {emailErrors[index] ? emailErrorMessage : ""}
              {roleErrors[index] ? roleErrorMessage : ""}
            </Grid2>
            {index !== 0 && (
              <>
                {index === emailVals.length - 1 && (
                  <Grid2 className={styles.addIcon}>
                    <IconButton
                      className={styles.icon}
                      icon={AddIcon}
                      onClick={handleAddEmail}
                    />
                  </Grid2>
                )}
              </>
            )}
          </>
        ))}
      </Grid2>
      {maxEmailsMessage && (
        <Grid2 className={styles.errorText}>{maxEmailsMessage}</Grid2>
      )}
      {/* <Grid2 className={styles.inviteBtn}>
        <CustomButton onClick={onInviteClick} disabled={isInviteDisabled}>
          {loading ? <CircularProgress size={20} color="#ffffff" /> : "Invite"}
        </CustomButton>
      </Grid2> */}
    </>
  );
}

export default InviteMember;
